import { createContext, useState, useEffect, useContext, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import {
  EmployeeGetApiById,
  getUserById,
  companyViewByIdApi,
  CandidateGetByIdApi,
  scheduleAutoRefresh,
  registerContextUpdater,
} from "../Utils/Axios";
import { useDispatch } from "react-redux";
import { setProfileImage, clearProfileImage } from "../Redux/ProfileImageSlice";
import { toast } from "react-toastify";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [company, setCompany] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const dispatch = useDispatch();

  const normalizeRoles = (roles) => {
    if (!roles) {
      toast.error("No Roles Found");
      return [];
    }
    return Array.isArray(roles) ? roles : [roles];
  };
  useEffect(() => {
    registerContextUpdater(setAuthUser);
  }, []);
  // decode + build user object
  const buildAuthUser = (token) => {
    try {
      const decoded = jwtDecode(token);
      const roles = normalizeRoles(decoded.roles);

      return {
        userId: decoded.sub,
        roles,
        company: decoded.company || null,
        employeeId: decoded.employee || null,
        resourceType: decoded.resourceType || null,
        isEmsAdmin: roles.includes("ems_admin"),
        isCompanyAdmin: decoded.resourceType === "company_admin",
        isAccountant: decoded.resourceType === "Accountant",
        isHR: decoded.resourceType === "HR",
        isAdmin: decoded.resourceType === "Admin",
        isEmployee: decoded.resourceType === "employee",
        isCandidate: decoded.resourceType === "candidate",
      };
    } catch (err) {
      console.error("Invalid token decode", err);
      return null;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsInitialized(true);
      return;
    }

    const decodedUser = buildAuthUser(token);
    if (!decodedUser) {
      localStorage.removeItem("token");
      setIsInitialized(true);
      return;
    }

    // check expiry
    const decoded = jwtDecode(token);
    if (decoded.exp * 1000 < Date.now()) {
      localStorage.removeItem("token");
      setIsInitialized(true);
      return;
    }

    scheduleAutoRefresh();
    setAuthUser(decodedUser);
  }, []);

  useEffect(() => {
    if (!authUser) {
      setEmployee(null);
      setCompany(null);
      return;
    }

    const fetchEmployeeOrUserOrCandidate = async (userId) => {
      try {
        const empRes = await EmployeeGetApiById(userId);
        return empRes?.data?.data;
      } catch { }

      try {
        const userRes = await getUserById(userId);
        const userData = userRes?.data?.data;
        return Array.isArray(userData) && userData.length > 0 ? userData[0] : userData;
      } catch { }

      try {
        const candidateRes = await CandidateGetByIdApi(userId);
        return candidateRes?.data?.data;
      } catch (err) {
        return null;
      }
    };

    const fetchDetails = async () => {
      try {
        const empData = await fetchEmployeeOrUserOrCandidate(authUser.userId);
        setEmployee(empData);

        // Dispatch profile image if employee and image exists
        if (authUser.resourceType === "employee" && empData?.profileImage) {
          dispatch(setProfileImage(empData.profileImage));
        }

        const companyId = empData?.companyId || authUser.company;
        if (companyId) {
          try {
            const compRes = await companyViewByIdApi(companyId);
            setCompany(compRes?.data?.data);

            // Dispatch company image for administrators
            if (["company_admin", "hrm", "Admin"].includes(authUser.resourceType) && compRes?.data?.data?.imageFile) {
              dispatch(setProfileImage(compRes.data.data.imageFile));
            }
          } catch (err) {
          }
        }
      } catch (err) {
      } finally {
        setIsInitialized(true);
      }
    };

    fetchDetails();
  }, [authUser]);

  const [activeBranch, setActiveBranch] = useState(() => {
    const storedBranch = localStorage.getItem("activeBranch");
    try {
      return storedBranch ? JSON.parse(storedBranch) : null;
    } catch (e) {
      console.error("Failed to parse stored branch", e);
      return null;
    }
  });

  const switchBranch = useCallback((branch) => {
    setActiveBranch(branch);
    localStorage.setItem("activeBranch", JSON.stringify(branch));
  }, []);

  // ... existing code ...

  const setAuthDetails = (accessToken, refreshToken) => {
    localStorage.setItem("token", accessToken);
    if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
    const user = buildAuthUser(accessToken);
    setAuthUser(user);
    // Note: We don't clear activeBranch here immediately to allow persistence across refreshes, 
    // but LayOut.jsx should validate if the active branch belongs to the current user.
  };

  const clearAuthDetails = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("activeBranch"); // Clear active branch on logout
    setAuthUser(null);
    setEmployee(null);
    setCompany(null);
    setActiveBranch(null);
    setIsInitialized(true);
    dispatch(clearProfileImage()); // Clear profile photo from Redux
  };

  const login = (token) => setAuthDetails(token);
  const logout = () => clearAuthDetails();

  const hasRole = (role) => authUser?.roles?.includes(role);
  const hasAnyRole = (roles) => roles.some((r) => hasRole(r));
  const isResourceType = (type) => authUser?.resourceType === type;

  return (
    <AuthContext.Provider
      value={{
        authUser,
        setAuthUser,
        employee,
        company,
        activeBranch, // Export activeBranch
        switchBranch, // Export switchBranch
        isInitialized,
        login,
        logout,
        hasRole,
        hasAnyRole,
        isResourceType,
        setAuthDetails,
        clearAuthDetails,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
